import React, { useState, useEffect } from "react";
import styled from "styled-components";
import EngagementListCard from "./EngagementListCard";
import EngagementListFilterBar from "./EngagementListFilterBar";
import BasePage from "../BasePage";
import CurrentUser from "../../apiResources/CurrentUser";
import Engagement, { EngagementStatus } from "../../apiResources/Engagement";
import Job, { JobType, JobStatus } from "../../apiResources/Job";
import Application from "../../apiResources/Application";
import Feedback from "../../apiResources/Feedback";
import { usePagination, useFlash, useDidMountEffect } from "../../hooks";
import Pagination from "../library/Pagination";
import Loader from "../library/Loader";
import Container from "../library/Container";
import FlashMessage from "../shared/general/FlashMessage";
import cache from "../../cache";
import EngagementsAccept from "../../apiResources/EngagementsAccept";
import { isFeatureEnabled } from "../../helpers";

const states = [
  "accepted",
  "pending",
  "disengaged",
  "rejected",
  "filled",
  "closed",
] as const;

type Status = (typeof states)[number];

export type FilterData = {
  status: Status[];
  jobType: JobType[];
};

function defaultFilters(localUser: CurrentUser) {
  const result: FilterData = { status: [], jobType: [] };

  if (
    isFeatureEnabled("displayPartnerEngagementsInYourJobs") &&
    localUser?.isPartnerUser
  ) {
    result.status = ["accepted", "pending"];
  } else {
    result.status = ["accepted"];
  }

  result.jobType = [JobType.PERMANENT];

  return result;
}

const EngagementListPage = (props: CurrentUser) => {
  const initial = getInitialData(props);

  const pagination = usePagination({ page: initial.page });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(initial.query);
  const [filters, setFilters] = useState<FilterData>(initial.filters);

  const [engagements, setEngagements] = useState<Engagement[]>(
    initial.engagements
  );
  const [totalEngagements, setTotalEngagements] = useState(
    initial.totalEngagements
  );
  const [user, setUser] = useState<CurrentUser>(initial.user);

  const params = new URLSearchParams(window.location.search);
  const candidateId = params.get("candidate_id");

  const fetchEngagements = async () => {
    setLoading(true);

    const localUser = user ?? (await CurrentUser.first()).data;
    setUser(localUser);

    const response = await Engagement.where({
      status: getStatus(filters),
      job_type: filters.jobType,
      job_status: getJobStatus(filters),
      job_title_or_employer_name: { match: query },
      exclude_pending:
        !isFeatureEnabled("displayPartnerEngagementsInYourJobs") &&
        localUser.isPartnerUser,
    })
      .includes({
        job: [
          "applications",
          "employer",
          "feedback_timescale",
          "sector",
          "currency",
        ],
      })
      .order({ created_at: "desc" })
      .page(pagination.page)
      .per(pagination.per)
      .stats({ total: "count" })
      .all();

    setEngagements(response.data);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    setTotalEngagements(response.meta.stats.total.count);
    setLoading(false);

    cache.set("engagement-list", {
      query,
      filters,
      page: pagination.page,
      results: response.data,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      total: response.meta.stats.total.count as number,
      user: localUser,
    });
  };

  useEffect(() => {
    if (!initial.cached) {
      void fetchEngagements();
    }
  }, []);

  useDidMountEffect(() => {
    void fetchEngagements();
  }, [filters, pagination.page, query]);

  useDidMountEffect(() => {
    pagination.setPage(1);
  }, [filters, query]);

  const isLoading = loading || !user;

  const flash = useFlash();

  const onJobDisengage = () => {
    void fetchEngagements();
    flash.show({ message: "Job disengaged" });
  };

  const acceptEngagement = async (engagementId: string) => {
    const accept = new EngagementsAccept({
      engagementId,
    });
    const success = await accept.save();

    if (success) {
      void fetchEngagements();
      flash.show({ message: "Success! You can now start working on the job" });
    }
  };

  const title = candidateId ? "Submit Candidate" : "Your Jobs";

  return (
    <BasePage title={title}>
      <Container>
        <FlashMessage
          message={flash.message}
          success
          showFlashMessage={flash.visible}
        />

        <EngagementListFilterBar
          filters={filters}
          initialQuery={query}
          onQueryChange={setQuery}
          onFiltersChange={setFilters}
          filteringEnabled={!candidateId}
          user={user}
        />

        {isLoading && <Loader text="Loading jobs..." />}

        {!isLoading && engagements.length === 0 && (
          <NoResults>Oops, there are no jobs matching your search!</NoResults>
        )}

        {!isLoading && engagements.length !== 0 && (
          <div id="engagements-list">
            {engagements.map((engagement) => (
              <EngagementListCard
                key={engagement.id}
                engagement={engagement}
                user={user}
                filteredCandidateId={candidateId}
                onDisengage={onJobDisengage}
                onAccept={() => acceptEngagement(engagement.id)}
              />
            ))}
            <Pagination pagination={pagination} total={totalEngagements} />
          </div>
        )}
      </Container>
    </BasePage>
  );
};

function getInitialData(currentUser: CurrentUser) {
  const cached = cache.get("engagement-list");

  const localUser = cached?.user ?? currentUser;

  const results = cached?.results ?? [];

  return {
    cached: Boolean(cached),
    filters: cached?.filters ?? defaultFilters(localUser),
    query: cached?.query ?? "",
    page: cached?.page ?? null,
    engagements: results.map(
      ({ job, ...data }) =>
        new Engagement({
          ...data,
          job: new Job({
            ...job,
            applications: job.applications.map(
              (application) => new Application(application)
            ),
            engagements: job.engagements.map(
              (engagement) => new Engagement(engagement)
            ),
            feedbacks: job.feedbacks.map((feedback) => new Feedback(feedback)),
          }),
        })
    ),
    totalEngagements: cached?.total ?? 0,
    user: localUser,
  };
}

const NoResults = styled.h2`
  display: flex;
  margin: 2rem 0;
  color: ${({ theme }) => theme.colors.text};
`;

function getStatus(filters: FilterData) {
  const result: EngagementStatus[] = [];

  if (filters.status.includes("accepted")) {
    result.push(EngagementStatus.ACCEPTED);
  }

  if (filters.status.includes("pending")) {
    result.push(EngagementStatus.PENDING);
  }

  if (filters.status.includes("disengaged")) {
    result.push(EngagementStatus.DISENGAGED);
  }

  if (filters.status.includes("rejected")) {
    result.push(EngagementStatus.REJECTED);
  }

  return result;
}

function getJobStatus(filters: FilterData) {
  const result: JobStatus[] = [];

  if (
    filters.status.includes("accepted") ||
    filters.status.includes("pending")
  ) {
    result.push(
      JobStatus.NEW,
      JobStatus.OPEN,
      JobStatus.INTERVIEW,
      JobStatus.OFFER
    );
  }

  if (filters.status.includes("filled")) {
    result.push(JobStatus.FILLED);
  }

  if (filters.status.includes("closed")) {
    result.push(JobStatus.EXPIRED, JobStatus.CLOSED);
  }

  return result;
}

export default EngagementListPage;
